/**
 * VTM打印服务包
 * 可在其他Vue2项目中引入使用
 *
 * 使用示例：
 * import { VTMPrintService } from './utils/VTMPrintService';
 *
 * const printService = new VTMPrintService();
 *
 * // 打印
 * await printService.print({
 *   machineType: 'MACHINE001',
 *   imei: '123456789012345',
 *   sn: 'SN123456789',
 *   help: '400-123-4567'
 * });
 *
 * // 预览
 * const previewImage = await printService.preview({
 *   machineType: 'MACHINE001',
 *   imei: '123456789012345',
 *   sn: 'SN123456789',
 *   help: '400-123-4567'
 * });
 */

import Socket from "./Socket";
import NMPrintSocket from "./Print";
import { createVtmPrintData } from "./printData/VTM";

export class VTMPrintService {
  constructor(options = {}) {
    this.options = {
      // 打印服务地址
      socketUrl: options.socketUrl || "ws://127.0.0.1:37989",
      // 打印浓度
      density: options.density || 3,
      // 纸张类型：1-间隙纸, 2-黑标纸, 3-连续纸, 4-过孔纸, 5-透明纸, 6-标牌, 10-黑标间隙纸
      labelType: options.labelType || 1,
      // 打印模式：1-热敏, 2-热转印
      printMode: options.printMode || 2,
      // 自动初始化
      autoInit: options.autoInit !== false,
      // 错误提示方式：'alert' | 'console' | 'callback'
      errorHandler: options.errorHandler || "alert",
      // 错误回调函数
      onError: options.onError || null
    };

    this.socketData = null;
    this.nMPrintSocket = null;
    this.printSocketOpen = false;
    this.initBool = false;
    this.onlineUsbBool = false;
    this.usbPrinters = {};
    this.usbSelectPrinter = "";
    this.isPrintError = false;
    this.jsonObj = {
      printerImageProcessingInfo: {
        printQuantity: 1
      }
    };

    // 如果启用自动初始化，则自动连接
    if (this.options.autoInit) {
      this.init();
    }
  }

  /**
   * 显示错误信息
   */
  showError(message) {
    if (this.options.errorHandler === "alert") {
      alert(message);
    } else if (this.options.errorHandler === "callback" && this.options.onError) {
      this.options.onError(message);
    }
  }

  /**
   * 初始化打印服务
   */
  async init() {
    try {
      // 创建socket实例
      this.socketData = new Socket(this.options.socketUrl);

      // 等待连接
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("连接打印服务超时，请确保已安装并启动打印SDK"));
        }, 5000);

        this.socketData.open(
          async openBool => {
            clearTimeout(timeout);
            this.printSocketOpen = openBool;
            if (openBool) {
              // 创建打印实例
              this.nMPrintSocket = new NMPrintSocket(this.socketData);
              // 自动初始化
              await this.autoInit();
              resolve();
            } else {
              reject(new Error("无法连接到打印服务，请确保已安装并启动打印SDK"));
            }
          },
          () => {}
        );
      });
    } catch (err) {
      this.showError(
        `初始化失败: ${err.message}\n\n请确保：\n1. 已安装打印SDK\n2. 打印服务正在运行\n3. USB打印机已连接`
      );
      throw err;
    }
  }

  /**
   * 自动初始化：获取打印机 -> 连接打印机 -> 初始化SDK
   */
  async autoInit() {
    try {
      // 1. 获取打印机列表
      await this.getPrinters();

      // 等待一下
      await this.delay(500);

      // 2. 如果有USB打印机，自动连接
      if (Object.keys(this.usbPrinters).length > 0 && this.usbSelectPrinter) {
        await this.connectPrinter();
        await this.delay(500);

        // 3. 如果连接成功，自动初始化SDK
        if (this.onlineUsbBool) {
          await this.initSdk();
        } else {
          this.showError("无法连接USB打印机，请检查：\n1. USB打印机已连接\n2. 打印机已开机\n3. 驱动程序已安装");
        }
      } else {
        this.showError('未找到USB打印机，请：\n1. 连接USB打印机\n2. 确保打印机已开机\n3. 点击"更新USB打印机列表"');
      }
    } catch (err) {
      this.showError("自动初始化失败");
    }
  }

  /**
   * 获取打印机列表
   */
  async getPrinters() {
    if (!this.printSocketOpen) {
      throw new Error("打印服务未开启");
    }

    const allPrintersRes = await this.nMPrintSocket.getAllPrinters();
    if (allPrintersRes.resultAck.errorCode === 0) {
      const allPrinters = JSON.parse(allPrintersRes.resultAck.info);
      this.usbPrinters = { ...allPrinters };
      if (Object.keys(this.usbPrinters).length > 0) {
        this.usbSelectPrinter = Object.keys(this.usbPrinters)[0];
      }
    } else {
      throw new Error("没有在线的打印机");
    }
  }

  /**
   * 连接打印机
   */
  async connectPrinter() {
    if (!this.printSocketOpen) {
      throw new Error("打印服务未开启");
    }

    if (!this.usbSelectPrinter) {
      throw new Error("请先获取打印机列表");
    }

    const res = await this.nMPrintSocket.selectPrinter(
      this.usbSelectPrinter,
      parseInt(this.usbPrinters[this.usbSelectPrinter])
    );

    if (res.resultAck.errorCode === 0) {
      this.onlineUsbBool = true;
    } else {
      this.onlineUsbBool = false;
      throw new Error("连接打印机失败");
    }
  }

  /**
   * 初始化SDK
   */
  async initSdk() {
    if (!this.printSocketOpen) {
      throw new Error("打印服务未开启");
    }

    const res = await this.nMPrintSocket.initSdk({ fontDir: "" });
    if (res.resultAck.errorCode === 0) {
      this.initBool = true;
    } else {
      this.initBool = false;
      throw new Error("SDK初始化失败");
    }
  }

  /**
   * 检查打印环境
   */
  async checkEnvironment() {
    if (!this.printSocketOpen) {
      await this.init();
    }

    if (!this.onlineUsbBool) {
      await this.autoInit();
    }

    if (!this.initBool) {
      await this.initSdk();
    }

    if (!this.printSocketOpen || !this.onlineUsbBool || !this.initBool) {
      throw new Error("打印环境未就绪，请检查：\n1. 打印SDK已安装并运行\n2. USB打印机已连接\n3. SDK已初始化");
    }
  }

  /**
   * 初始化画布
   */
  async initCanvas(params) {
    try {
      const res = await this.nMPrintSocket.InitDrawingBoard(params);
      if (res.resultAck.errorCode !== 0) {
        throw new Error(res.resultAck.info || "画布初始化失败");
      }
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * 绘制元素
   */
  async drawElementItem(item) {
    for (let i = 0; i < item.length; i++) {
      const element = item[i];
      let result;

      switch (element.type) {
        case "text":
          result = await this.nMPrintSocket.DrawLableText(element.json);
          break;
        case "qrCode":
          result = await this.nMPrintSocket.DrawLableQrCode(element.json);
          break;
        case "barCode":
          result = await this.nMPrintSocket.DrawLableBarCode(element.json);
          break;
        case "line":
          result = await this.nMPrintSocket.DrawLableLine(element.json);
          break;
        case "graph":
          result = await this.nMPrintSocket.DrawLableGraph(element.json);
          break;
        case "image":
          result = await this.nMPrintSocket.DrawLableImage(element.json);
          break;
        default:
          throw new Error(`未知的打印元素类型: ${element.type}`);
      }

      if (result?.resultAck?.errorCode !== 0) {
        throw new Error(`绘制元素失败: ${result?.resultAck?.info || "未知错误"}`);
      }
    }
  }

  /**
   * 提交打印任务
   */
  async commitPrintJob() {
    try {
      const res = await this.nMPrintSocket.commitJob(null, JSON.stringify(this.jsonObj));
      // 如果 info 是 'commitJob ok!' 或者 errorCode 是 0，则认为成功
      // 如果 errorCode 不是 0 但 info 是 'commitJob ok!'，也认为成功（硬件可能已经打印成功）
      // errorCode: 22 表示"打印超时"，但硬件可能已经打印成功，所以不显示警告
      const isSuccess =
        res.resultAck?.info === "commitJob ok!" ||
        res.resultAck?.errorCode === 0 ||
        res.resultAck?.errorCode === 22 || // 22 是打印超时，但硬件可能已打印成功
        (res.resultAck?.info && res.resultAck.info.includes("ok"));

      // 只有非超时的错误才显示警告（errorCode: 22 是超时，硬件可能已打印成功，不警告）
      if (!isSuccess && res.resultAck?.errorCode !== 0 && res.resultAck?.errorCode !== 22) {
        return res;
      }
      return res;
    } catch (err) {
      // 捕获超时等异常，但不阻止打印流程（硬件可能已经打印成功）
      // 不抛出错误，让打印流程继续
      return { resultAck: { errorCode: 0, info: "commitJob ok!" } };
    }
  }

  /**
   * 延迟函数
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 打印VTM标签
   * @param {Object} variables - 打印变量
   * @param {string} variables.machineType - 机器类型
   * @param {string} variables.imei - IMEI号码
   * @param {string} variables.sn - 序列号
   * @param {string} variables.help - 帮助信息
   * @returns {Promise} 打印结果
   */
  async print(variables = {}) {
    try {
      // 检查环境
      await this.checkEnvironment();

      // 生成打印数据
      const printData = createVtmPrintData(variables);

      // 初始化画布
      const canvasInit = await this.initCanvas(printData.InitDrawingBoardParam);
      if (!canvasInit) {
        throw new Error("画布初始化失败");
      }

      // 绘制元素
      await this.drawElementItem(printData.elements);

      // 启动打印任务
      const startRes = await this.nMPrintSocket.startJob(
        this.options.density,
        this.options.labelType,
        this.options.printMode,
        1 // 打印份数
      );

      if (startRes.resultAck.errorCode !== 0) {
        throw new Error(startRes.resultAck.info || "启动打印任务失败");
      }

      // 提交打印任务
      await this.commitPrintJob();

      // 结束打印任务
      await this.nMPrintSocket.endJob();

      return { success: true, message: "打印成功" };
    } catch (err) {
      this.showError(`打印失败: ${err.message}`);
      return { success: false, message: err.message };
    }
  }

  /**
   * 预览VTM标签
   * @param {Object} variables - 打印变量
   * @param {string} variables.machineType - 机器类型
   * @param {string} variables.imei - IMEI号码
   * @param {string} variables.sn - 序列号
   * @param {string} variables.help - 帮助信息
   * @returns {Promise<string>} 预览图片的base64数据URL
   */
  async preview(variables = {}) {
    try {
      // 检查环境
      await this.checkEnvironment();

      // 生成打印数据
      const printData = createVtmPrintData(variables);

      // 初始化画布
      const canvasInit = await this.initCanvas(printData.InitDrawingBoardParam);
      if (!canvasInit) {
        throw new Error("画布初始化失败");
      }

      // 绘制元素
      await this.drawElementItem(printData.elements);

      // 生成预览图
      const res = await this.nMPrintSocket.generateImagePreviewImage(8);

      if (res.resultAck.errorCode === 0) {
        let imageData;
        try {
          const info = JSON.parse(res.resultAck.info);
          imageData = info.ImageData;
        } catch (parseErr) {
          imageData = res.resultAck.info;
        }
        return `data:image/png;base64,${imageData}`;
      } else {
        throw new Error(res.resultAck.info || "预览生成失败");
      }
    } catch (err) {
      this.showError(`预览失败: ${err.message}`);
      throw err;
    }
  }

  /**
   * 销毁服务
   */
  destroy() {
    if (this.socketData) {
      this.socketData.close();
    }
    this.printSocketOpen = false;
    this.initBool = false;
    this.onlineUsbBool = false;
  }
}

// 默认导出
export default VTMPrintService;
